import { useState, type ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Separator,
} from '@predoc/cow';
import { useCancelPatientRequest } from '../../hooks/patientsAPI';

type CancelRequestDialogRenderProps = {
  openDialog: () => void;
};

export const CancelRequestDialog = ({
  patientId,
  requestId,
  children,
}: {
  patientId: string;
  requestId: string;
  children: (props: CancelRequestDialogRenderProps) => ReactNode;
}) => {
  const cancelRequestMutation = useCancelPatientRequest();
  const [open, setOpen] = useState(false);

  const handleOpenDialog = () => setOpen(true);

  const handleConfirmCancel = () => {
    if (cancelRequestMutation.isPending) return;

    cancelRequestMutation.mutate(
      { patientId, requestId },
      {
        onSuccess: () => setOpen(false),
      },
    );
  };

  return (
    <>
      {children({ openDialog: handleOpenDialog })}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby="cancel-request-dialog" className="p-0">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle>Cancel Request</DialogTitle>
          </DialogHeader>
          <Separator />
          <div className="p-4">
            <Alert variant="destructive">
              <AlertCircle className="size-5" />
              <AlertTitle>Confirm Cancel Request</AlertTitle>
              <AlertDescription>
                You&apos;re about to request cancellation of an open request.
                Requests that are currently processing cannot be canceled but
                every effort will be made to cancel before processing begins.
              </AlertDescription>
            </Alert>
          </div>
          <Separator />
          <DialogFooter className="flex justify-end p-2 pt-0">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Back
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmCancel}
              disabled={cancelRequestMutation.isPending}
            >
              Cancel Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
